import React, { useState, useEffect } from "react";
import { setTitle } from "../helpers/MetaTag";
import Button from "../components/form/Button";
import Select from "../components/form/Select";
import Search from "../components/form/Search";
import Table from "../components/elements/table/Table";
import moment from "moment";
import ReactPaginate from "react-paginate";
import { DragDropContext } from "react-beautiful-dnd";
import { reorder } from "../helpers";
import toastr from "toastr";
import { useParams } from "react-router-dom";

import { postData } from "../services/api";
import { useTranslation } from "react-i18next";

const Options = () => {
  setTitle("Succession Planning Software for your Business with Succession Now | Options");
  const { t } = useTranslation();
  const { heading_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortValue, setSortValue] = useState(-1);
  const [sortQuery, setSortQuery] = useState("");
  const [status, setStatus] = useState("");
  const [noOfPage, setNoOfPage] = useState(0);
  const [optionData, setOptionData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [startDataCount,setStartDataCount] = useState(0);
  const [endDataCount,setEndDataCount] = useState(0);
  const [actionValue, setActionValue] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);

  const [headingDetails, setHeadingDetails] = useState({});

  useEffect(() => {
    if (sortKey && sortValue) {
      setSortQuery({ [sortKey]: sortValue });
    } else {
      setSortQuery("");
    }
  }, [sortKey, sortValue]);

  useEffect(() => {
    if (actionValue && actionValue.button_type === "status") {
      const loadStatusChange = async () => {
        setIsUpdate(false);
        try {
          const statusData = await postData("option/status-change", {
            id: actionValue?.row_id,
            status: actionValue?.current_status,
          });
          if (statusData.data) {
            setOptionData((prevOptionData) =>
              prevOptionData.map((it) => {
                if (it?._id === statusData.data?._id) {
                  return {
                    ...it,
                    status: statusData.data?.status,
                  };
                } else {
                  return it;
                }
              })
            );
            setIsUpdate(true);
          } else {
            toastr.error(statusData.message);
          }
        } catch (error) {
          toastr.error(error.message);
        }
      };
      loadStatusChange();
    } else if (actionValue && actionValue.button_type === "delete") {
      const loadDeleteRecord = async () => {
        setIsUpdate(false);
        try {
          const statusData = await postData("option/delete", {
            id: actionValue?.row_id,
          });
          if (statusData.data) {
            // const index = optionData.findIndex((item) => item._id === actionValue?.row_id);
            // if (index !== -1) {
            //   optionData.splice(index, 1);
            //   setOptionData(
            //     optionData.map((it) => {
            //       return it;
            //     })
            //   );
            // }
            setOptionData((prevOptionData) => prevOptionData.filter((item) => item._id !== actionValue.row_id));
            setIsUpdate(true);
          } else {
            toastr.error(statusData.message);
          }
        } catch (error) {
          toastr.error(error.message);
        }
      };
      loadDeleteRecord();
    }
    //console.log('actionValue',actionValue)
  }, [actionValue]);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      try {
        const result = await postData("option/list", {
          keyword,
          limit,
          offset,
          sortQuery,
          status,
          heading_id,
        });
        if (result.data) {
          setOptionData(
            result.data.map((value) => {
              const noteField =
                headingDetails && headingDetails.type === "concern"
                  ? ""
                  : { _id: 3, type: "text", data: value?.note };

              return {
                _id: value?._id,
                rowData: [
                  { _id: 1, type: "text", data: value?.name },
                  { _id: 2, type: "text", data: value?.point },
                  noteField,
                  {
                    _id: 4,
                    type: "text",
                    data: value?.created_at
                      ? moment(value?.created_at).format("Do MMM YYYY")
                      : "N/A",
                  },
                  {
                    _id: 5,
                    type: "status",
                    statusLabel:
                      value?.status === 1 ? t("active") : t("deactivated"),
                    statusType: value?.status === 1 ? "success" : "warning",
                  },
                  {
                    _id: 6,
                    type: "action",
                    statusLabel:
                      value?.status === 1 ? t("active") : t("deactivated"),
                    statusType: value?.status === 1 ? "success" : "warning",
                  },
                ].filter(Boolean), // to avoid empty strings in the array
              };
            })
          );
        }
        setTotalDataCount(result.count);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    const loadHeadingDetails = async () => {
      try {
        const result = await postData("heading/details", { id: heading_id });
        if (result.data) {
          setHeadingDetails(result.data);
        }
      } catch (err) {
        console.error(err.message);
      }
    };

    if (heading_id) {
      loadHeadingDetails();
    }

    loadOptions();
  }, [
    keyword,
    limit,
    offset,
    sortQuery,
    status,
    isUpdate,
    heading_id,
    headingDetails,
    t,
  ]);


  useEffect(() => {
    setNoOfPage(Math.ceil(totalDataCount / limit));
    const startItem = Number((totalDataCount>0)?1:0);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem)
  }, [limit, totalDataCount]);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * limit) % totalDataCount;
    console.log(`User requested page number ${event.selected}, which is offset ${newOffset}`);
    setOffset(newOffset);
    const startItem = Number(newOffset + 1);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem)
  };

  const tableHeadData = [{ _id: 1, name: t("name"), value: "name", align: "left", isSort: true, isFilter: false }, { _id: 2, name: t("point"), value: "weight", align: "left", isSort: true, isFilter: false }, headingDetails && headingDetails.type === "predictors" ? { _id: 3, name: "Note", value: "note", align: "left", isSort: true, isFilter: false } : "", { _id: 4, name: t("created_date"), value: "created_at", align: "left", isSort: false, isFilter: false }, { _id: 5, name: t("status"), value: "status", align: "left", isSort: false, isFilter: false }, { _id: 6, name: t("action"), value: "", align: "right", isSort: false, isFilter: false }];

  const handleOnDragEnd = async (result) => {
    const items = reorder(optionData, result.source.index, result.destination.index);
    setOptionData(items);
    const res = await postData("performance/ordering", {
      items: items,
    });
    if (res.status && res.status === 200) {
      toastr.success(res.message);
    } else {
      toastr.error(res.message);
    }
  };

  return (
    <>
      <div className="relative flex justify-between items-center mb-4 flex-wrap sm:flex-nowrap gap-y-4">
        <div className="text-xl md:text-3xl text-slate-800 font-bold">
          {t("options")} {headingDetails && headingDetails.name ? " " + t("for") + " " + headingDetails.name : ""}
        </div>
        <div className="sm:ml-auto flex justify-end items-center">
          <Button buttonType={"button"} buttonIcon={"fa-light fa-plus"} buttonIconPosition={"left"} buttonLabel={t("add_option")} buttonHasLink={true} buttonLink={"/add-option/" + heading_id} />
        </div>
      </div>
      <div className="relative flex justify-between items-center mb-4 flex-wrap sm:flex-nowrap gap-y-2">
        <div className="relative">
          <Select
            xPlacement={"left"}
            dropdownButtonClass={"!bg-white"}
            selectedValue={limit}
            dropdownData={[
              { _id: 1000000, label: t("all_items"), value: 1000000 },
              ...[
                { _id: 10, label: "10 " + t("items"), value: 10 },
                { _id: 20, label: "20 " + t("items"), value: 20 },
                { _id: 30, label: "30 " + t("items"), value: 30 },
                { _id: 50, label: "50 " + t("items"), value: 50 },
                { _id: 100, label: "100 " + t("items"), value: 100 },
              ],
            ]}
            getSelectedValue={(e) => setLimit(e.value)}
          />
        </div>
        <div className="relative w-full sm:w-auto sm:ml-auto flex sm:justify-end flex-wrap sm:flex-nowrap items-center gap-1 sm:gap-3">
          <div className="relative w-full sm:w-auto">
            <Search placeholder={t("search")} setInput={setKeyword} />
          </div>
          {/* <div className="relative">
            <Select 
            xPlacement={'right'} 
            dropdownButtonClass={'!bg-white'} 
            dropdownData={[
                { _id: '', label: 'All Type' },
                { _id: 2, label: 'User', value: 'User' },
                { _id: 3, label: 'Provider', value: 'Provider' },
            ]}
            />
            </div> */}
          <div className="relative">
            <Select
              xPlacement={"right"}
              dropdownButtonClass={"!bg-white"}
              transitionClass={"!w-auto"}
              selectedValue={status}
              dropdownData={[
                { _id: "", label: t("all_status") },
                { _id: 1, label: t("active"), value: 1 },
                { _id: 2, label: t("deactivated"), value: 2 },
              ]}
              getSelectedValue={(e) => setStatus(e.value)}
            />
          </div>
        </div>
      </div>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <div className="w-full bg-white rounded-md shadow overflow-hidden">
          <Table tableData={optionData} tableHeadData={tableHeadData} totalRecord={totalDataCount} loading={loading} getSortValue={setSortValue} getSortKey={setSortKey} editUrl={"/edit-option/" + heading_id} isStatusUpdate={true} isDeleteEnabled={true} isDraggable={false} getActionValue={(obj) => setActionValue(obj)} />
        </div>
      </DragDropContext>
      {noOfPage > 1 ? (
        <div className="flex justify-center sm:justify-between my-4">
          <div><p>{t('showing')}: {startDataCount} to {endDataCount} of {totalDataCount}</p></div>
          <ReactPaginate breakLabel="..." breakClassName="w-8 h-8 flex justify-center items-center rounded hover:bg-slate-200" nextLabel={<i className="fa-regular fa-fw fa-chevron-right"></i>} nextClassName="w-8 h-8 flex justify-center items-center rounded hover:bg-slate-200" onPageChange={handlePageClick} pageRangeDisplayed={3} pageCount={noOfPage} containerClassName="flex justify-center items-center text-slate-80 text-sm font-medium mt-8 lg:mt-0 space-x-2" activeClassName="hover:bg-teal-500 bg-teal-500 text-white" pageLinkClassName="flex justify-center items-center w-full h-full" previousLabel={<i className="fa-regular fa-fw fa-chevron-left"></i>} pageClassName="w-8 h-8 flex justify-center items-center rounded hover:bg-slate-200" previousClassName="w-8 h-8 flex justify-center items-center rounded hover:bg-slate-200" renderOnZeroPageCount={null} />
        </div>
      ):(
        <div className="flex justify-center sm:justify-between my-4">
          {(totalDataCount > 0) ? <div><p>{t('showing')}: {startDataCount} to {endDataCount} of {totalDataCount}</p></div>:''}
        </div>
      )}
    </>
  );
};

export default Options;
